import type { ReactNode } from 'react';
import React, {
  useState,
  useCallback,
  createContext,
  useContext,
  useSyncExternalStore,
  useEffect,
} from 'react';
import { deferred, noop } from '@/shared/utils';
import { SCLoadingOverlay } from '@lib/sc-eone-ui/src/loading_overlay';
import { createPortal } from 'react-dom';

interface ModalProps<T = any> {
  close: () => void;
  result: (value?: T | Error) => void;
  zIndex: number;
}

interface ModalContextType {
  show: <T>(content: React.FC<ModalProps<T>>, index?: number) => Promise<T | undefined>;
  showAlive: (name: string, content: React.FC<ModalProps>, index?: number) => () => void;
  closeAllModals: () => void;
  showLoadingOverlay: (Label?: string) => void;
  hideLoadingOverlay: () => void;
}

interface ModalState {
  modals: Array<{
    visible: boolean;
    index: number;
    zIndex: number;
    content: React.FC<ModalProps>;
    result: (value?: any) => void;
    close: () => void;
  }>;
  highestZIndex: number;
}

let listeners: (() => void)[] = [];
const visibleMap = new Map<string, { show: boolean; index: number }>();

export const visibleStore = {
  show(name: string, index: number) {
    visibleMap.set(name, { show: true, index });
    emitChange();
  },
  hide(name: string) {
    visibleMap.set(name, { ...(visibleMap.get(name) ?? { show: false, index: -1 }), show: false });
    emitChange();
  },
  remove(name: string) {
    visibleMap.delete(name);
    emitChange();
  },
  subscribe(listener) {
    listeners = [...listeners, listener];
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  },
  getSnapshot() {
    return visibleMap;
  },
};

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const modalVisibleMap = useSyncExternalStore(visibleStore.subscribe, visibleStore.getSnapshot);

  const [overlay, setOverlay] = useState<{ Show: boolean; Label?: string }>({ Show: false });
  const [modalState, setModalState] = useState<ModalState>({
    modals: [],
    highestZIndex: 100,
  });

  const getNextIndex = useCallback((prevModalState: ModalState) => {
    return Math.max(...prevModalState.modals.map((modal) => modal.index), 0) + 1;
  }, []);

  const closeModal = useCallback((index: number) => {
    setModalState((prevState) => ({
      modals: prevState.modals.filter((modal) => modal.index !== index),
      highestZIndex: prevState.highestZIndex,
    }));
  }, []);

  const closeAllModals = useCallback(() => {
    setModalState({
      modals: [],
      highestZIndex: 100,
    });
  }, []);

  const show = useCallback<
    <T = any>(content: React.FC<ModalProps<T>>, index?: number) => Promise<T | undefined>
  >((content, index) => {
    const { promise, resolve, reject } = deferred<any>();
    setModalState((prevState) => {
      const modalIndex = index !== undefined ? index : getNextIndex(prevState);
      const newZIndex = prevState.highestZIndex + 1;
      promise.finally(() => {
        closeModal(modalIndex);
      });
      return {
        modals: [
          ...prevState.modals,
          {
            visible: true,
            index: modalIndex,
            zIndex: newZIndex,
            content,
            result: (value?: any) => {
              if (value instanceof Error) {
                reject(value);
              } else {
                resolve(value);
              }
            },
            close: () => resolve(null),
          },
        ],
        highestZIndex: newZIndex,
      };
    });
    return promise;
  }, []);

  const showAlive = useCallback<
    (name: string, content: React.FC<ModalProps>, index?: number) => () => void
  >((name, content, index) => {
    if (modalVisibleMap.has(name)) {
      const modalIndex = modalVisibleMap.get(name)?.index ?? -1;
      visibleStore.show(name, modalIndex);
      return () => {
        closeModal(modalIndex);
      };
    }
    let modalIndex = -1;
    setModalState((prevState) => {
      modalIndex = index !== undefined ? index : getNextIndex(prevState);
      const newZIndex = prevState.highestZIndex + 1;
      return {
        modals: [
          ...prevState.modals,
          {
            visible: true,
            index: modalIndex,
            zIndex: newZIndex,
            content: (props) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const visible = useSyncExternalStore(
                visibleStore.subscribe,
                () => visibleStore.getSnapshot().get(name)?.show ?? false,
              );
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(
                () => () => {
                  visibleStore.remove(name);
                },
                [],
              );
              return <div hidden={!visible}>{React.createElement(content, { ...props })}</div>;
            },
            result: noop,
            close: () => {
              visibleStore.hide(name);
            },
          },
        ],
        highestZIndex: newZIndex,
      };
    });
    visibleStore.show(name, modalIndex);
    return () => {
      closeModal(modalIndex);
    };
  }, []);

  const showLoadingOverlay = useCallback((Label?: string) => {
    setOverlay({ Show: true, Label });
  }, []);

  const hideLoadingOverlay = useCallback(() => {
    setOverlay({ Show: false });
  }, []);

  const modalElements = modalState.modals.map((modal) =>
    modal.visible
      ? createPortal(
          React.createElement(modal.content, {
            key: modal.index,
            zIndex: modal.zIndex,
            result: modal.result,
            close: modal.close,
          }),
          document.body,
        )
      : null,
  );

  return (
    <ModalContext.Provider
      value={{ show, showAlive, closeAllModals, showLoadingOverlay, hideLoadingOverlay }}
    >
      {children}
      {modalElements}
      <SCLoadingOverlay Visible={overlay.Show} Label={overlay.Label} />
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextType {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
